<template>
  <div>
    <SubHeader :title="getNodeName(node)" :subtitle="subtitle" :tabs="detailTabs" v-model:activeTab="detailTab">
      <template v-if="!isRoot" #actions>
        <GenericButton
          v-if="isEditable"
          class="btn-dark"
          :icon="IconPencil"
          icon-pos="start"
          data-testid="vbudget-detail-edit"
          @click="emit('edit', node)">
          {{ $t('btn.edit') }}
        </GenericButton>
        <GenericButton
          v-if="isDeletable"
          class="btn-white"
          :icon="IconTrash"
          data-testid="vbudget-detail-delete"
          @click="emit('delete', node)">
          {{ $t('btn.delete') }}
        </GenericButton>
      </template>
    </SubHeader>

    <!-- Details Tab -->
    <div v-if="detailTab === 'details'" class="p-subpage">
      <div class="row g-4">
        <div class="col-md-6">
          <!-- Budget Usage Bar -->
          <div class="budget-usage">
            <div class="budget-usage__bar">
              <div
                v-if="barSegments.spent > 0"
                class="budget-usage__segment budget-usage__segment--spent"
                :style="{ width: barSegments.spent + '%' }"></div>
              <div
                v-if="barSegments.allocated > 0"
                class="budget-usage__segment budget-usage__segment--allocated"
                :style="{ width: barSegments.allocated + '%' }"></div>
            </div>
            <div class="budget-usage__legend">
              <div class="budget-usage__legend-item">
                <span class="budget-usage__dot budget-usage__dot--spent"></span>
                <span class="text-muted">{{ $t('billing.vbudgets.spent') }}</span>
                <span class="fw-semibold">{{ formatCurrency(node.availability.total_spent, currency) }}</span>
              </div>
              <div v-if="isRoot" class="budget-usage__legend-item">
                <span class="budget-usage__dot budget-usage__dot--allocated"></span>
                <span class="text-muted">{{ $t('billing.vbudgets.allocated_label') }}</span>
                <span class="fw-semibold">{{ formatCurrency(allocatedAmount, currency) }}</span>
              </div>
              <div class="budget-usage__legend-item">
                <span class="budget-usage__dot budget-usage__dot--unallocated"></span>
                <span class="text-muted">{{
                  isRoot ? $t('billing.vbudgets.unallocated') : $t('billing.vbudgets.available')
                }}</span>
                <span class="fw-semibold" data-testid="vbudget-detail-available">{{
                  formatCurrency(
                    isRoot ? node.availability.available_unallocated : node.availability.available_now,
                    currency
                  )
                }}</span>
              </div>
            </div>
          </div>

          <!-- Details -->
          <SectionHeader :title="$t('billing.vbudgets.details')" />
          <PropertyList :items="detailProperties" class="mb-4" />

          <!-- Auto Selection -->
          <div v-if="showAutoSelection">
            <SectionHeader :title="$t('billing.vbudgets.auto_selection')" />
            <p class="text-muted mb-3">{{ $t('billing.vbudgets.auto_selection_description') }}</p>
            <ChannelReferenceSelector
              v-if="node.vbudget?.data.reference_channels?.length"
              :model-value="node.vbudget.data.reference_channels"
              :label="$t('billing.vbudgets.form.reference_channels')"
              disabled />
          </div>
        </div>
      </div>
    </div>

    <!-- Transactions Tab -->
    <div v-if="detailTab === 'transactions'">
      <SubBudgetTransactionsTable
        v-if="!isRoot && node?.vbudget?.data.id"
        :endpoint="transactionsEndpoint"
        :key="node.vbudget.data.id" />
      <EmptyState v-else :title="$t('billing.budget.transactions.empty')" :icon="IconReceipt" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { IconPencil, IconTrash, IconReceipt } from '@tabler/icons-vue';

import SubHeader from '@/components/header/SubHeader.vue';
import SectionHeader from '@/components/header/SectionHeader.vue';
import PropertyList from '@/components/common/PropertyList.vue';
import EmptyState from '@/components/common/EmptyState.vue';
import GenericButton from '@/components/generic/GenericButton.vue';
import SubBudgetTransactionsTable from '@/components/budget/SubBudgetTransactionsTable.vue';
import ChannelReferenceSelector from '@/components/form/ChannelReferenceSelector.vue';

import translator from '@/locale/translator';
import { formatCurrency } from '@/locale/format';
import { getBudgetNodeName } from '@/helpers/budgetUtils';

import type { IBudgetTreeNode } from '@/types/budget';
import type { PropertyItem } from '@/components/common/PropertyList.vue';

interface Props {
  node: IBudgetTreeNode;
  isRoot: boolean;
  isEditable: boolean;
  isDeletable: boolean;
  currency: string;
  transactionsEndpoint: string;
  subtitle?: string;
  detailProperties: PropertyItem[];
  barSegments: { spent: number; allocated: number };
  allocatedAmount: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  edit: [node: IBudgetTreeNode];
  delete: [node: IBudgetTreeNode];
}>();

/* --------------------------------- STATE -------------------------------- */
const detailTab = ref('details');

const getNodeName = getBudgetNodeName;

/* -------------------------------- COMPUTED -------------------------------- */
const detailTabs = computed(() => [
  { label: translator.get('billing.vbudgets.tab.details'), value: 'details' },
  { label: translator.get('billing.budget.transactions.title'), value: 'transactions' },
]);

const showAutoSelection = computed(() => !props.isRoot && props.node?.vbudget?.data);

/* -------------------------------- WATCHERS -------------------------------- */
watch(
  () => props.node,
  () => {
    detailTab.value = 'details';
  }
);
</script>

<style scoped>
.budget-usage {
  margin-bottom: 2rem;
}

.budget-usage__bar {
  display: flex;
  height: 12px;
  border-radius: 6px;
  background-color: #e9ecef;
  overflow: hidden;
  margin-bottom: 0.75rem;
}

.budget-usage__segment {
  transition: width 0.3s ease;
}

.budget-usage__segment--spent {
  background-color: var(--tblr-danger, #d63939);
  border-radius: 6px 0 0 6px;
}

.budget-usage__segment--allocated {
  background-color: var(--tblr-primary, #007aff);
}

.budget-usage__legend {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.budget-usage__legend-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 13px;
}

.budget-usage__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.budget-usage__dot--spent {
  background-color: var(--tblr-danger, #d63939);
}

.budget-usage__dot--allocated {
  background-color: var(--tblr-primary, #007aff);
}

.budget-usage__dot--unallocated {
  background-color: #e9ecef;
}
</style>
